import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { bindActionCreators } from 'redux'
import { useSelector, useDispatch } from 'react-redux'
import { authActions, getUsuarioLogado } from '../auth/AuthStore'
import { STATUS_MATRICULA } from '../models/MatriculaModel'
import logoBlue from '../assets/auth/logoBlue.svg'
import { AuthFooter } from '../auth/AuthPageLayout'
import { AUTH_COLORS, AUTH_MOBILE_BREAKPOINT } from '../auth/AuthForm'
import axios, { CursoUsuarioResumo } from '../common/axios'
import { CursoCard } from './CursoCard'
import { AppAvatar } from '../components/AppAvatar'

export function SelecionaCurso() {
  const { Layout } = SelecionaCurso

  return (
    <Layout id="Layout">
      <MenuAppBar />
      <ListaCursosAluno />
      <AuthFooter />
    </Layout>
  )
}

SelecionaCurso.Layout = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-y: auto;
  background: #ffffff;
  color: ${AUTH_COLORS.blue};
  font-family: Rubik, sans-serif;
  display: flex;
  flex-direction: column;
`

function MenuAppBar() {
  const { Layout, Logo } = MenuAppBar

  return (
    <Layout>
      <Logo src={logoBlue} alt="New Driver" />
      <Menu />
    </Layout>
  )
}

MenuAppBar.Layout = styled.header`
  width: 100%;
  height: 90px;
  padding: 24px 30px;
  border-bottom: 1px solid rgba(235, 235, 235, 1);
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    height: 71px;
    padding: 20px 24px 20px 20px;
  }
`

MenuAppBar.Logo = styled.img`
  width: 91px;
  height: auto;
  display: block;
`

function Menu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const dispatch = useDispatch()
  const usuario = useSelector(getUsuarioLogado)
  const actions = bindActionCreators(authActions, dispatch)
  const { MenuContainer, UserButton, Avatar, UserName, MenuOptions } = Menu
  const menuRef = useRef<HTMLDivElement>(null)
  const nomeAluno = getPrimeiroNome(usuario?.nome)

  function handleLogout() {
    setIsMenuOpen(false)
    actions.doLogout()
    location.href = '/'
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('click', handleClickOutside)
    return () => window.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <MenuContainer ref={menuRef}>
      <UserButton
        type="button"
        aria-label="Abrir menu do usuário"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        <Avatar className="avatar-circle" usuario={usuario} circular tamanho={44} />
        <UserName>{nomeAluno || 'Aluno'}</UserName>
      </UserButton>
      {isMenuOpen ? (
        <MenuOptions>
          <li onClick={handleLogout}>Sair</li>
        </MenuOptions>
      ) : null}
    </MenuContainer>
  )
}

Menu.MenuContainer = styled.div`
  position: relative;
`

Menu.UserButton = styled.button`
  max-width: 220px;
  border: 0;
  border-radius: 999px;
  padding: 0;
  background: transparent;
  color: ${AUTH_COLORS.blue};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;

  :hover .avatar-circle {
    box-shadow: 0 0 0 3px rgba(18, 42, 65, 0.08);
  }
`

Menu.Avatar = styled(AppAvatar)`
  flex: 0 0 auto;
  transition: box-shadow 0.2s ease;

  @media (max-width: ${AUTH_MOBILE_BREAKPOINT}) {
    width: 36px;
    height: 36px;
  }
`

Menu.UserName = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${AUTH_COLORS.blue};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    display: none;
  }
`

Menu.MenuOptions = styled.ul`
  min-width: 120px;
  margin: 8px 0 0;
  padding: 8px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 2;
  background: #ffffff;
  border: 1px solid rgba(209, 215, 224, 1);
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(18, 42, 65, 0.12);
  list-style: none;

  li {
    border-radius: 4px;
    color: ${AUTH_COLORS.blue};
    cursor: pointer;
    font-size: 14px;
    line-height: 20px;
    padding: 8px 10px;
  }

  li:hover {
    background: rgba(249, 236, 231, 1);
    color: ${AUTH_COLORS.orange};
  }
`

function ListaCursosAluno() {
  const [cursosAluno, setCursosAluno] = useState<CursoUsuarioResumo[]>([])
  const [fetching, setFetching] = useState(true)
  const [message, setMessage] = useState('')
  const { Main, Section, SectionHeader, SectionTitle, CursosGrid, StateMessage } = ListaCursosAluno

  useEffect(() => {
    async function getCursos() {
      try {
        const cursosUsuario = await axios.Cursos.getCursosUsuarioResumo()
        setCursosAluno(cursosUsuario)
      } catch {
        setMessage('Ocorreu um erro ao buscar os cursos.')
      } finally {
        setFetching(false)
      }
    }

    getCursos()
  }, [])

  const cursos = useMemo(() => {
    return [...cursosAluno].sort((a, b) => getCursoOrder(a) - getCursoOrder(b))
  }, [cursosAluno])

  const cursosEmAndamento = useMemo(
    () => cursos.filter((curso) => curso.statusDetran !== STATUS_MATRICULA.FINALIZADO),
    [cursos]
  )

  const cursosConcluidos = useMemo(
    () => cursos.filter((curso) => curso.statusDetran === STATUS_MATRICULA.FINALIZADO),
    [cursos]
  )

  if (fetching) {
    return (
      <Main>
        <StateMessage>Carregando...</StateMessage>
      </Main>
    )
  }

  if (message) {
    return (
      <Main>
        <StateMessage>{message}</StateMessage>
      </Main>
    )
  }

  if (!cursos.length) {
    return (
      <Main>
        <StateMessage>Nenhum curso encontrado.</StateMessage>
      </Main>
    )
  }

  return (
    <Main>
      {cursosEmAndamento.length ? (
        <Section>
          <SectionHeader>
            <SectionTitle>Cursos em andamento</SectionTitle>
          </SectionHeader>
          <CursosGrid id="MenuCursoWrap">
            {cursosEmAndamento.map((curso) => (
              <CursoCard key={curso.matriculaId || curso.id} curso={curso} />
            ))}
          </CursosGrid>
        </Section>
      ) : null}

      {cursosConcluidos.length ? (
        <Section>
          <SectionHeader>
            <SectionTitle>Curso concluído</SectionTitle>
          </SectionHeader>
          <CursosGrid>
            {cursosConcluidos.map((curso) => (
              <CursoCard key={curso.matriculaId || curso.id} curso={curso} />
            ))}
          </CursosGrid>
        </Section>
      ) : null}
    </Main>
  )
}

ListaCursosAluno.Main = styled.main`
  width: 100%;
  min-height: 0;
  flex: 1 0 auto;
  padding: 80px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;

  @media (max-width: 1023px) {
    padding: 60px 24px;
  }

  @media (max-width: 640px) {
    padding: 50px 16px;
  }
`

ListaCursosAluno.Section = styled.section`
  width: 100%;
  max-width: 1148px;
`

ListaCursosAluno.SectionHeader = styled.div`
  width: 100%;
`

ListaCursosAluno.SectionTitle = styled.h2`
  margin: 0;
  border-left: 4px solid ${AUTH_COLORS.blue};
  padding-left: 10px;
  color: ${AUTH_COLORS.blue};
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
`

ListaCursosAluno.CursosGrid = styled.div`
  width: 100%;
  max-width: 1148px;
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 368px));
  justify-content: flex-start;
  gap: 24px;

  @media (max-width: 1023px) {
    max-width: 760px;
    grid-template-columns: repeat(2, minmax(0, 368px));
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

ListaCursosAluno.StateMessage = styled.p`
  margin: 0;
  color: ${AUTH_COLORS.blue};
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`

function getCursoOrder(curso: CursoUsuarioResumo) {
  if (['MATRICULADO', 'AGUARDANDO_MATRICULA'].includes(curso.statusDetran)) return 0
  if (curso.statusDetran === 'FINALIZADO') return 2
  return 1
}

function getPrimeiroNome(nome?: string) {
  return nome?.trim().split(/\s+/)[0] ?? ''
}
